import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Play, Pause, Music, Check, ShoppingCart } from 'lucide-react';
import { useApp } from '../context/AppContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const BeatDetailPage = () => {
  const { beat_id } = useParams();
  const { addToCart, setCurrentBeat, setIsPlaying, currentBeat, isPlaying } = useApp();
  const [beat, setBeat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLicense, setSelectedLicense] = useState('lease');

  useEffect(() => {
    fetchBeat();
  }, [beat_id]);

  const fetchBeat = async () => {
    try {
      const response = await axios.get(`${API}/beats/${beat_id}`);
      setBeat(response.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handlePlay = () => {
    if (currentBeat?.beat_id === beat.beat_id) {
      setIsPlaying(!isPlaying);
    } else {
      setCurrentBeat(beat);
      setIsPlaying(true);
    }
  };

  const licenses = [
    {
      type: 'lease',
      name: 'Lease',
      price: beat?.lease_price,
      features: ['MP3 file', 'Up to 10k streams', 'Non-exclusive', '1 music video']
    },
    {
      type: 'premium',
      name: 'Premium Lease',
      price: beat?.premium_price,
      features: ['MP3 + WAV files', 'Up to 100k streams', 'Non-exclusive', 'Unlimited music videos', 'Trackout stems']
    },
    {
      type: 'exclusive',
      name: 'Exclusive',
      price: beat?.exclusive_price,
      features: ['All files + stems', 'Unlimited streams', 'Full ownership', 'Beat removed from store', 'Certificate included']
    }
  ];

  const handleAddToCart = () => {
    const license = licenses.find(l => l.type === selectedLicense);
    addToCart({
      item_id: beat.beat_id,
      item_type: 'beat',
      license_type: selectedLicense,
      title: `${beat.title} (${license.name})`,
      price: license.price
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!beat) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <p className="text-neutral-500">Beat not found</p>
      </div>
    );
  }

  const isCurrentPlaying = currentBeat?.beat_id === beat.beat_id && isPlaying;

  return (
    <div className="min-h-screen pt-24 pb-32 px-6 lg:px-12" data-testid="beat-detail-page">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Beat Info */}
          <div>
            <div className="glass-card p-2 mb-6">
              <div className="relative aspect-square rounded-xl overflow-hidden">
                {beat.image_url ? (
                  <img src={beat.image_url} alt={beat.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-neutral-900 flex items-center justify-center">
                    <Music size={64} className="text-[#D4AF37]/30" />
                  </div>
                )}
                <button
                  onClick={handlePlay}
                  className="absolute bottom-6 right-6 w-16 h-16 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B8941F] flex items-center justify-center glow-primary hover:scale-105 transition-transform"
                  data-testid="detail-play-btn"
                >
                  {isCurrentPlaying ? <Pause size={28} fill="black" stroke="black" /> : <Play size={28} fill="black" stroke="black" className="ml-1" />}
                </button>
              </div>
            </div>

            <h1 className="text-3xl sm:text-4xl font-bold mb-4 text-white" style={{ fontFamily: 'Syne' }}>{beat.title}</h1>
            
            <div className="flex flex-wrap gap-4 mb-6 mono text-sm">
              <span className="px-3 py-1.5 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20">{beat.bpm} BPM</span>
              <span className="px-3 py-1.5 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20">{beat.key}</span>
              <span className="px-3 py-1.5 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20">{beat.genre}</span>
              <span className="px-3 py-1.5 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20">{beat.mood}</span>
            </div>

            {beat.tags && beat.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {beat.tags.map(tag => (
                  <span key={tag} className="text-xs text-neutral-500">#{tag}</span>
                ))}
              </div>
            )}
          </div>

          {/* License Selection */}
          <div>
            <h2 className="text-xl font-bold mb-6 text-white" style={{ fontFamily: 'Syne' }}>Select License</h2>
            
            <div className="space-y-4 mb-8">
              {licenses.map(license => (
                <div
                  key={license.type}
                  onClick={() => setSelectedLicense(license.type)}
                  className={`license-card cursor-pointer ${selectedLicense === license.type ? 'featured' : ''}`}
                  data-testid={`license-${license.type}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        selectedLicense === license.type ? 'border-[#D4AF37] bg-[#D4AF37]' : 'border-neutral-600'
                      }`}>
                        {selectedLicense === license.type && <Check size={12} className="text-black" />}
                      </div>
                      <span className="font-bold text-white" style={{ fontFamily: 'Syne' }}>{license.name}</span>
                    </div>
                    <span className="text-2xl font-bold gradient-text">${license.price} CAD</span>
                  </div>
                  <ul className="space-y-2">
                    {license.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-neutral-400">
                        <Check size={14} className="text-[#D4AF37] flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <button
              onClick={handleAddToCart}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="add-to-cart-btn"
            >
              <ShoppingCart size={18} />
              Add to Cart - ${licenses.find(l => l.type === selectedLicense)?.price} CAD
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BeatDetailPage;
